<template>
  <div>
    <div v-if="showHeader" class="section-header">
      <h4 v-if="canTagReviews" class="section-header__title">
        {{ $t("message.reviews.tags.reviewTags") }}
        <ReviewsCategoriesProcessingTooltip
          v-if="categorizationInProgress"
          :label="$t('message.sentiment.reviewsProcessingNumber3')"
        />
      </h4>

      <div v-if="noTagsSelected" class="message no-selected">
        {{ $t("message.reviews.tags.noTagsSelected") }}
      </div>
      <div v-if="noTagsAssigned" class="message no-selected">
        {{ $t("message.reviews.tags.noTagsAssigned") }}
      </div>
      <div v-if="noTagsAvailable" class="message">
        {{ $t("message.reviews.tags.noTagsAvailable") }}
        <span class="link-btn" @click="$emit('open-dialog')">{{
          $t("message.reviews.tags.defineTagsHere")
        }}</span>
      </div>
    </div>
    <v-skeleton-loader
      v-if="showLoader"
      class="loader"
      type="chip@4"
      indeterminate
      color="primary"
    ></v-skeleton-loader>
    <template v-else>
      <div class="tags-list-container" :class="{ 'mt-4': !canTagReviews }">
        <div class="tags-list">
          <template v-for="tag in sortedTags">
            <TagsManagerListItem
              v-model="tagsState[tag.id]"
              :key="tag.id"
              :tag="tag"
              :max-characters="15"
              :toggle-enabled="canTagReviews"
              :is-processing="categorizationInProgress || readOnly"
              @change="onTagToggle(tag.id, $event)"
              @change-sentiment="onSentimentChange(tag.id, $event.sentiment)"
            />
          </template>
        </div>
      </div>
    </template>

    <ui-dialog
      v-if="showAlert"
      :show="showAlert"
      persistent
      scrollable
      :closable="false"
      max-width="542"
      @input="closeAlertDialog"
    >
      <template #header>
        <h3>
          {{ $t("message.reviews.categories.sentimentChange") }}
        </h3>
      </template>
      <template #default>
        <p class="alert-text">
          {{ $t("message.reviews.categories.sentimentChangeAlert") }}
        </p>
      </template>

      <template #actions>
        <div class="d-flex justify-end w-100">
          <ui-button outlined color="primary" @click="closeAlertDialog">{{
            $t("message.common.cancel")
          }}</ui-button>
          <ui-button
            class="ml-2"
            color="primary"
            outlined
            @click="toggleTagHandler(true)"
          >
            {{ $t("message.reviews.categories.makeChange") }}
          </ui-button>
        </div>
      </template>
    </ui-dialog>
  </div>
</template>

<script>
import TagsManagerListItem from "@/components/Tags/TagsManagerListItem";
import reviewsService from "@/services/reviews";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import ReviewsCategoriesProcessingTooltip from "@/components/Reviews/Categories/ReviewsCategoriesProcessingTooltip.vue";
import { SENTIMENT_NAMES } from "@/enums/sentiment";
import reviewTagsMixin from "@/mixins/review-tags";

const CATEGORIZATION_STATUSES = {
  DONE: null,
  IN_PROGRESS: 1,
  FAILED: 2
};
const SENTIMENT_SOURCE = {
  MANUAL: 1,
  AUTO: 2
};

export default {
  name: "ReviewTagsSelector",
  components: {
    TagsManagerListItem,
    ReviewsCategoriesProcessingTooltip
  },
  mixins: [permissionsInfo, reviewTagsMixin],
  props: {
    showHeader: {
      type: Boolean,
      default: true
    },
    isLoading: {
      type: Boolean,
      default: true
    },
    availableTags: {
      type: Array,
      default: () => []
    },
    review: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    selectedOnly: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    sortedTags() {
      return this.sortedTagsList;
    },
    showLoader() {
      return !this.contentRendered || this.isLoading;
    },
    activeTags() {
      return this.review.tags || [];
    },
    hasAvailableTags() {
      return !!this.availableTags.length;
    },
    hasSelectedTags() {
      return Object.values(this.tagsState).some(({ isActive }) => isActive);
    },
    categorizationInProgress() {
      return (
        this.review.categorization_status == CATEGORIZATION_STATUSES.IN_PROGRESS
      );
    },
    noTagsSelected() {
      return (
        !this.hasSelectedTags && this.hasAvailableTags && this.canTagReviews
      );
    },
    noTagsAssigned() {
      return this.selectedOnly && this.readOnly && !this.hasSelectedTags;
    },
    noTagsAvailable() {
      return (
        !this.showLoader &&
        !this.hasAvailableTags &&
        this.canTagReviews &&
        !this.readOnly
      );
    }
  },
  data() {
    return {
      tagsState: {},
      contentRendered: false,
      showAlert: false,
      sentimentData: null,
      sortedTagsList: []
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.contentRendered = true;
    });
  },
  watch: {
    availableTags: {
      handler() {
        this.setTagsState();
      },
      immediate: true
    }
  },

  methods: {
    closeAlertDialog() {
      this.showAlert = false;
      const { tagId, sentiment } = this.sentimentData;
      const lastSentiment = this.tagsState[tagId].sentiment;

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEW_CATEGORIES_CONFIRM_AUTO_ASSIGNED_CHANGE,
        sentiment_old: SENTIMENT_NAMES[lastSentiment],
        sentiment_new: undefined,
        action: "canceled",
        category_name: this.availableTags.find(item => item.id === tagId).name,
        category_removed: sentiment ? true : false
      });
      this.sentimentData = null;
    },
    async onSentimentChange(tagId, sentiment) {
      await this.patchReviewCategory(tagId, sentiment);
    },
    async patchReviewCategory(tagId, sentiment) {
      await reviewsService.patchReviewCategory(
        this.review.id,
        tagId,
        sentiment
      );
    },
    setTagsState() {
      this.tagsState = this.availableTags.reduce((obj, tag) => {
        const localTag = this.activeTags.find(i => i.id === tag.id);
        return {
          ...obj,
          [tag.id]: {
            isActive: !!localTag,
            sentiment: localTag && localTag.sentiment,
            isAutoAssigned: localTag && localTag.is_auto_assigned
          }
        };
      }, {});

      // Sort tags only when they are loaded
      this.sortedTagsList = [...this.availableTags].sort((a, b) => {
        const aActive = this.tagsState[a.id].isActive;
        const bActive = this.tagsState[b.id].isActive;
        return bActive - aActive;
      });
    },

    async onTagToggle(tagId, { isActive, sentiment, isAutoAssigned }) {
      this.sentimentData = { tagId, isActive, sentiment, isAutoAssigned };

      if (isAutoAssigned) {
        this.showAlert = true;
        return;
      }
      await this.toggleTagHandler();
    },
    async toggleTagHandler(isDialog = false) {
      const { tagId, isActive, sentiment, isAutoAssigned } = this.sentimentData;
      const lastSentiment = this.tagsState[tagId].sentiment;
      this.tagsState[tagId] = { isActive, sentiment, isAutoAssigned };
      let categoryRemovedEvent;
      if (isActive) {
        await reviewsService.assignTag(this.review.id, tagId);
        this.tagsState[tagId].isAutoAssigned = false;
        if (sentiment !== undefined) {
          categoryRemovedEvent = false;

          await this.onSentimentChange(tagId, { sentiment });
        }
      } else {
        this.tagsState[tagId].isAutoAssigned = false;
        await reviewsService.unassignTag(this.review.id, tagId);
        categoryRemovedEvent = true;
      }

      if (isDialog) {
        this.$gtm.trackCustomEvent({
          event: GTM_EVENT_NAME.REVIEW_CATEGORIES_CONFIRM_AUTO_ASSIGNED_CHANGE,
          action: "confirmed",
          review_id: this.review.id,
          category_name: this.availableTags.find(item => item.id === tagId)
            .name,
          sentiment_old: SENTIMENT_NAMES[lastSentiment],
          sentiment_new: SENTIMENT_NAMES[sentiment],
          category_removed: categoryRemovedEvent
        });
      }

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEW_CATEGORIES_SELECT_DESELECT,
        name: this.availableTags.find(i => i.id === tagId).name,
        action: this.tagsState[tagId] ? "select" : "deselect",
        review_id: this.review.id,
        sentiment: SENTIMENT_NAMES[sentiment]
      });

      this.sentimentData = null;
      this.showAlert = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.loader {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing;
  padding-left: $spacing * 2;

  ::v-deep .v-skeleton-loader__chip {
    height: 46px;
    border-radius: 44px;
    background: rgba(0, 0, 0, 0.09);
  }
}

.tags-list-container {
  margin: $spacing * -1.25;
}

.tags-list {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: $spacing * 1.25;
}

.section-header {
  position: relative;
  margin-left: $spacing * 2;

  &__title {
    display: flex;
    align-items: center;
    gap: $spacing;
  }
}

h4 {
  @include sectionTitle();
  font-weight: 500;
  margin: $spacing * 3 0;
}

.message {
  @include font-size(11px);
  line-height: 14px;
  font-weight: 400;
  white-space: pre-line;
}

.no-selected {
  position: absolute;
  bottom: $spacing * -2;
}

.link-btn {
  font-weight: 500;
  color: $primary;
  cursor: pointer;
  user-select: none;

  &:hover {
    text-decoration: underline;
  }
}

.tags-readonly {
  @include font-size(12px);
  line-height: 16px;
  font-weight: normal;
  color: $dark-blue-3;
  margin-left: $spacing * 2;
}
.alert-text {
  color: $dark-blue-3;
  @include font-size(16px);
}
</style>
